// frontend/src/pages/employees/TeamTab.jsx
import React, { useState } from "react";
import EntriesDrawer from "./EntriesDrawer";
import { hours as fmtHours, money } from "./format";
import LeaveRoster from "./LeaveRoster";
import { useAnalytics } from "./useAnalytics";

const panel = {
  background: "white", borderRadius: 12, padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: 24,
};
const th = { padding: "10px 8px", fontWeight: 600, textAlign: "right",
             borderBottom: "2px solid #e5e7eb", fontSize: 12,
             textTransform: "uppercase", color: "#6b7280" };
const thLeft = { ...th, textAlign: "left" };
const td = { padding: "10px 8px", textAlign: "right",
             borderBottom: "1px solid #f3f4f6" };
const tdLeft = { ...td, textAlign: "left" };

const nameButton = {
  background: "none", border: "none", padding: 0, font: "inherit",
  fontWeight: 600, color: "#3b82f6", cursor: "pointer", textAlign: "left",
};

const numberButton = {
  background: "none", border: "none", padding: 0, font: "inherit",
  color: "#3b82f6", cursor: "pointer", textAlign: "right",
};

const hoursCell = {
  display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8,
};
const hoursBarTrack = {
  width: 60, height: 6, background: "#e5e7eb", borderRadius: 3,
  overflow: "hidden", flexShrink: 0,
};
const hoursBarFill = {
  display: "block", height: "100%", background: "#3b82f6",
};

/**
 * The firm, one row per employee, for the selected period.
 *
 * This is the old Overview table minus the columns nobody read:
 * Non-Billable (the row already carries Hours and Billable), Clients
 * count (a count of a count) and Billable % -- utilisation is explicitly
 * out of scope, spec section 12.
 */
export default function TeamTab({ startDate, endDate, onSelectEmployee }) {
  const roster = useAnalytics("breakdown/", {
    by: "employee", start_date: startDate, end_date: endDate,
  });
  const leave = useAnalytics("leave/", { as_of: endDate });
  const [drawer, setDrawer] = useState(null);

  const rows = (roster.data && roster.data.rows) || [];
  const totals = roster.data && roster.data.totals;
  // (unassigned) (r.id === null) draws no bar -- it is not clickable and
  // has no hoursBarFill element at all (see below) -- so it must not be
  // allowed to set the scale every real bar is measured against.
  const maxHours = rows.reduce(
    (m, r) => (r.id === null ? m : Math.max(m, Number(r.hours) || 0)), 0);

  const balanceOf = {};
  ((leave.data && leave.data.rows) || []).forEach((r) => {
    balanceOf[r.user_id] = r.balance;
  });

  // Opens this row's entries drawer. Billable and Amount both narrow to
  // billable time: money_sum() on the backend counts only
  // billable_code="BILLABLE" entries, so the amount comes from exactly
  // those rows, and the drawer -- which has no money column -- has to
  // compare its hours total against billable_hours to match.
  const openDrawer = (row, kind) => {
    const params = { employee: row.id, start_date: startDate, end_date: endDate };
    let expectedHours = row.hours;
    if (kind === "Billable" || kind === "Amount") {
      params.billable = "BILLABLE";
      expectedHours = row.billable_hours;
    }
    setDrawer({ title: `${row.label} — ${kind}`, params, expectedHours });
  };

  return (
    <div>
      <div style={panel}>
        <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16 }}>Team</h3>

        {roster.loading && <div style={{ color: "#6b7280" }}>Loading…</div>}
        {roster.error && (
          <div role="alert" style={{ color: "#b91c1c" }}>{roster.error}</div>
        )}

        {!roster.loading && !roster.error && rows.length === 0 && (
          <div style={{ color: "#6b7280" }}>
            No time logged in this period.
          </div>
        )}

        {!roster.loading && !roster.error && rows.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={thLeft}>Employee</th>
                  <th style={th}>Hours</th>
                  <th style={th}>Billable</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Vacation balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdLeft}>
                      {/* `breakdown?by=employee` emits id: null for entries
                          with no creator. A click on that row has no
                          employee to drill into -- onSelectEmployee(null,
                          "(unassigned)") is discarded upstream -- so it is
                          plain text, not a button that does nothing. */}
                      {r.id === null ? r.label : (
                        <button
                          type="button" style={nameButton}
                          onClick={() => onSelectEmployee &&
                            onSelectEmployee(r.id, r.label)}
                        >
                          {r.label}
                        </button>
                      )}
                    </td>
                    <td style={td}>
                      {r.id === null ? fmtHours(r.hours) : (
                        <span style={hoursCell}>
                          <button
                            type="button" style={numberButton}
                            aria-label={`${r.label} Hours ${fmtHours(r.hours)} — show entries`}
                            onClick={() => openDrawer(r, "Hours")}
                          >
                            {fmtHours(r.hours)}
                          </button>
                          {/* Proportional to the largest Hours value among
                              the rendered rows. Decoration only -- the
                              number stays the thing a screen reader
                              announces. */}
                          <span
                            data-testid="hours-bar"
                            aria-hidden="true"
                            style={hoursBarTrack}
                          >
                            <span style={{
                              ...hoursBarFill,
                              // A correcting entry can make hours negative;
                              // Number(r.hours) / maxHours would then be a
                              // negative percentage, which is invalid CSS
                              // and falls back to "auto" -- rendering the
                              // smallest value as a full-width bar, the
                              // exact opposite of what the bar means.
                              width: maxHours > 0
                                ? `${Math.max(0, (Number(r.hours) / maxHours) * 100)}%`
                                : "0%",
                            }} />
                          </span>
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      {r.id === null ? fmtHours(r.billable_hours) : (
                        <button
                          type="button" style={numberButton}
                          aria-label={`${r.label} Billable ${fmtHours(r.billable_hours)} — show entries`}
                          onClick={() => openDrawer(r, "Billable")}
                        >
                          {fmtHours(r.billable_hours)}
                        </button>
                      )}
                    </td>
                    <td style={td}>
                      {r.id === null ? money(r.amount) : (
                        <button
                          type="button" style={numberButton}
                          aria-label={`${r.label} Amount ${money(r.amount)} — show entries`}
                          onClick={() => openDrawer(r, "Amount")}
                        >
                          {money(r.amount)}
                        </button>
                      )}
                    </td>
                    <td style={td}>
                      {/* A dash, not a zero: "not calculable" and "nil
                          balance" are different statements, and only one
                          of them is about the employee. */}
                      {balanceOf[r.id] !== undefined
                        ? fmtHours(balanceOf[r.id])
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot>
                  <tr data-testid="firm-total" style={{ fontWeight: 700 }}>
                    <td style={{ ...tdLeft, borderTop: "2px solid #e5e7eb" }}>
                      Firm
                    </td>
                    <td style={{ ...td, borderTop: "2px solid #e5e7eb" }}>
                      {fmtHours(totals.hours)}
                    </td>
                    <td style={{ ...td, borderTop: "2px solid #e5e7eb" }}>
                      {fmtHours(totals.billable_hours)}
                    </td>
                    <td style={{ ...td, borderTop: "2px solid #e5e7eb" }}>
                      {money(totals.amount)}
                    </td>
                    <td style={{ ...td, borderTop: "2px solid #e5e7eb" }}>
                      {/* Summed from the RENDERED rows, not leave/'s own
                          totals.balance -- that endpoint's total spans every
                          employee with a hire date, which is a different,
                          usually larger, population than "logged time this
                          period". A dash, not a partial sum, if any visible
                          row's own balance is absent: absent and zero are
                          different statements, same rule the per-row cell
                          follows, and silently dropping a person from the
                          total is its own kind of wrong number. */}
                      {(() => {
                        if (!leave.data) return "—";
                        const values = rows.map((r) => balanceOf[r.id]);
                        if (values.some((v) => v === undefined)) return "—";
                        const sum = values.reduce(
                          (s, v) => s + Number(v), 0);
                        return fmtHours(sum);
                      })()}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>
              Amount is billable time only.
            </div>
          </div>
        )}
      </div>

      {/* LeaveRoster owns the missing-hire-date warning -- it has more
          context (it sits beside the vacation table itself) and showing
          it here too just repeated the same banner twice on one page. */}

      {/* The ROSTER, not a one-person panel. LeavePanel would have shown
          rows[0] -- one arbitrary employee's balance with no name on it. */}
      <LeaveRoster
        asOf={endDate}
        startDate={startDate}
        endDate={endDate}
      />

      <EntriesDrawer
        open={Boolean(drawer)}
        title={drawer ? drawer.title : ""}
        params={drawer ? drawer.params : {}}
        expectedHours={drawer ? drawer.expectedHours : null}
        onClose={() => setDrawer(null)}
      />
    </div>
  );
}
